import { appHostPostPath } from "../../../functions/src/posts/appHostPrefix";

/** "Jan 2, 2026" — the blog's date format. Pinned to UTC so the SSR server and
 *  the browser (which may be in different timezones) format the same calendar
 *  day, avoiding a hydration mismatch. */
export function formatBlogDate(value: Date | string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** ISO yyyy-mm-dd for a <time datetime>. */
export function isoDate(value: Date | string | null): string | undefined {
  if (!value) return undefined;
  return new Date(value).toISOString().slice(0, 10);
}

/** Today's calendar day as ISO yyyy-mm-dd in UTC. The publication-date field
 *  defaults to this so "publish today" needs no manual entry (#440); UTC keeps
 *  it consistent with how a stored `publishedAt` is read back (`isoDate`). */
export function todayUtcIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Render a `/blog/<slug>` path for display or navigation. Persisted slugs (e.g.
 *  Hugo imports) aren't constrained to URL-safe characters — the schema allows
 *  any non-empty VARCHAR(255) and importHugoPosts only trims — so encode the
 *  segment to match the actual navigable path. Canonical slugs (`[a-z0-9-]`)
 *  are unchanged. */
export function blogPath(slug: string): string {
  return `/blog/${encodeURIComponent(slug)}`;
}

/** Render a custom-domain post path — the ORIGIN-ROOT `/<slug>` shape
 *  (2026-08-27 reshape). `blogPath` above stays the app-host `/blog/<slug>`
 *  shape, where the React routes serve the path-based blog. */
export function customDomainPostPath(slug: string): string {
  return `/${encodeURIComponent(slug)}`;
}

/** The path a post actually goes live at, for the workspace's blog: root-level
 *  `/<slug>` on a custom domain, app-relative `/blog/<slug>` otherwise.
 *
 *  The client-side counterpart to `postHrefFrom` in
 *  functions/src/blog/renderShared.ts, and it exists for the same reason: no
 *  call site should re-derive which shape applies. Every UI surface that shows
 *  an author where their post lives — the slug hint, the "will go live at"
 *  line, the publish confirmation — asks this one function, so a fourth surface
 *  added later cannot hardcode one host's shape. */
export function postPathFor(
  customDomain: string | null | undefined,
  slug: string,
  appHostPrefix?: string | null,
): string {
  if (appHostPrefix) return appHostPostPath(appHostPrefix, slug);
  return customDomain ? customDomainPostPath(slug) : blogPath(slug);
}

/** Rough reading time in minutes (~200 wpm), rounded up, min 1. Re-exported from the
 *  import-free leaf the hosted themes also call, so byline and composer cannot drift. */
export { readingMinutes } from "../../../functions/src/blog/readingMinutes";

/** Returns `url` only when it parses as an absolute http(s) URL, else undefined.
 *  Post-authored fields (e.g. author_url) reach an `<a href>` and JSON-LD; a
 *  `javascript:`/`data:` scheme there is a stored-XSS vector, so gate every such
 *  link through this. */
export function safeHttpUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }
  return parsed.protocol === "http:" || parsed.protocol === "https:"
    ? url
    : undefined;
}

/** The author's cover alt text (#1269), trimmed; "" when absent or blank, which
 *  means "decorative". Mirrors `coverImageAltText` in
 *  functions/src/blog/renderShared.ts — keep the two in step. */
export function coverImageAltText(
  alt: string | null | undefined,
): string {
  return alt?.trim() ?? "";
}

/** A post's cover image (#599) as a renderable `src`, else undefined. Mirrors
 *  the themed SSR renderer's `coverImageSrc`
 *  (functions/src/blog/renderShared.ts) — including WHY it is https-only rather
 *  than http(s), and the #629 host-trust decision it documents. Keep the two in
 *  step: the same stored value renders through both. */
export function coverImageSrc(
  url: string | null | undefined,
): string | undefined {
  if (!url) return undefined;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }
  return parsed.protocol === "https:" ? url : undefined;
}
